<template>
  <div
    ref="root"
    class="slider-group"
  >
    <button
      v-if="!isMobile"
      class="slider-group__button slider-group__button_prev"
      :class="{
        'slider-group__button_hidden': !slider?.isLeftArrowVisible,
      }"
      @click="handleClickPrev"
    >
      <span class="ui-icon-fz-medium ui-icon-arrow-left icon-secondary--text" />
    </button>
    <div
      class="slider-group__fog slider-group__fog_left"
      :class="{
        'slider-group__fog_visible': fog && fog !== 'right'
          && !slider?.isFullyScrolledToLeft && slider?.hasUserInteracted,
      }"
    />
    <div class="slider-group__slider">
      <div
        ref="container"
        class="slider-group__container d-flex align-end"
        :style="{
          'padding-left': `${paddingLeft ?? paddingX ?? 0}px`,
          'padding-right': `${paddingRight ?? paddingX ?? 0}px`,
        }"
      >
        <div
          v-for="(item, itemIndex) in items"
          ref="sliderItemRefs"
          :key="itemIndex"
          class="slider-group__item"
          :class="{
            'mr-2': itemIndex < items.length - 1,
          }"
          @click="handleClickItem($event, itemIndex)"
        >
          <slot
            v-bind="{ item, index: itemIndex, onClick }"
          />
        </div>
      </div>
    </div>
    <div
      class="slider-group__fog slider-group__fog_right"
      :class="{
        'slider-group__fog_visible': fog && fog !== 'left' && !slider?.isFullyScrolledToRight,
      }"
    />
    <button
      v-if="!isMobile"
      class="slider-group__button slider-group__button_next"
      :class="{
        'slider-group__button_hidden': !slider?.isRightArrowVisible,
      }"
      @click="handleClickNext"
    >
      <span class="ui-icon-fz-medium ui-icon-arrow-right icon-secondary--text" />
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  reactive,
  nextTick,
} from 'vue'
import useSlider from 'www/vue_components/common/SliderGroup/composables/useSlider'

const props = defineProps<{
  items: any[]
  paddingX?: number
  paddingLeft?: number
  paddingRight?: number
  activeIndex?: number
  fog?: boolean | 'left' | 'right'
  scrollToActive?: boolean
  noAutoPosition?: boolean
}>()

const emit = defineEmits(['slider-group:click-item'])

const isMobile = window.MOBILE_VERSION

const container = ref<HTMLDivElement>()
const root = ref<HTMLDivElement>()

const slider = ref<ReturnType<typeof useSlider> | null>(null)
const sliderItemRefs = ref<HTMLDivElement[]>()

// Инициализируем слайдер, когда отрендерится container
const unwatch = watch(container, async () => {
  if (container.value && root.value) {
    unwatch()

    await nextTick()
    await nextTick()

    slider.value = reactive(useSlider({
      root: root.value,
      container: container.value,
      items: computed(() => sliderItemRefs.value || []),
      paddingLeft: props.paddingLeft ?? props.paddingX ?? 0,
    }))

    if (!slider.value) {
      return
    }

    if (!isMobile) {
      slider.value.addEventListeners()
    }

    if (typeof props.activeIndex === 'number' && props.activeIndex > 0 && props.scrollToActive) {
      slider.value.scrollToItemByIndex(props.activeIndex)
    }
  }
})

watch(sliderItemRefs, value => {
  if (value && typeof props.activeIndex === 'number' && slider.value && props.scrollToActive) {
    slider.value.scrollToItemByIndex(props.activeIndex)
  }
}, { deep: true })

function handleClickItem(event: MouseEvent, index: number) {
  if (isMobile || slider.value?.isClick(event.pageX)) {
    if (!props.noAutoPosition && !props.items[index].disabled) {
      slider.value?.scrollToItemByIndex(index, true)
    }

    emit('slider-group:click-item', index)
  }
}

function onClick(event: MouseEvent, callback: () => void) {
  if (isMobile || slider.value?.isClick(event.pageX)) {
    callback()
  }
}

function handleClickNext() {
  slider.value?.scrollToNextItem()
}

function handleClickPrev() {
  slider.value?.scrollToPrevItem()
}
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.slider-group {
  position: relative;

  &__slider {
    overflow: hidden;
    user-select: none;
  }

  &__container {
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__button {
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: $ui-kit-bg-gray-0;
    transition: 0.2s ease-in-out;

    @include new-elevation-2;

    &_next {
      right: -16px;
    }

    &_prev {
      left: -16px;
    }

    &_hidden {
      opacity: 0;
      visibility: hidden;
    }
  }

  &__fog {
    pointer-events: none;
    z-index: 1;
      width: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    transition: 0.1s linear;

    &_visible {
      width: 48px;
    }

    &_left {
      left: 0;
      background: linear-gradient(
        90deg,
        #FFFFFF 0.71%,
        rgba(255, 255, 255, 0.8099) 11.66%,
        rgba(255, 255, 255, 0.6198) 21.74%,
        rgba(255, 255, 255, 0.3843) 31.54%,
        rgba(255, 255, 255, 0) 42.77%
      );
    }

    &_right {
      right: 0;
      background: linear-gradient(
        270deg,
        #FFFFFF 0.71%,
        rgba(255, 255, 255, 0.8099) 11.66%,
        rgba(255, 255, 255, 0.6198) 21.74%,
        rgba(255, 255, 255, 0.3843) 31.54%,
        rgba(255, 255, 255, 0) 42.77%
      );
    }
  }
}
</style>
